import {
  currencyFormat,
  dateDiffDays,
  html,
  icon,
  parseUserDate,
  render,
  ServiceAuthorizationCapListItem,
  serviceAuthorizationLimits,
  trashIcon,
  userUTCDateFormat
} from '@cumu/shared';
import {
  loc_actions,
  loc_amount,
  loc_balance,
  loc_delete,
  loc_endDate,
  loc_externalID,
  loc_externalUsage,
  loc_priorBalance,
  loc_startDate,
  loc_usage
} from '@cumu/strings';
import { controller, target } from '@github/catalyst';
import type AgGrid from 'ag-grid-enterprise';
import { FORM_BEFORE_VALIDATE_SUBMIT_EVENT_TYPE } from '../forms/behavior-element';
import {
  FORM_VALIDATE_EVENT_TYPE,
  FormValidationError
} from '../forms/validation';
import { getLocale } from '../locale';
import { dispatchChange } from './util';

declare const AG_GRID_LICENSE_KEY: string;
declare const agGrid: typeof AgGrid;

const locale = getLocale();

// const columnTypes: Record<string, AgGrid.ColTypeDef> = {};

@controller('service-authorization-caps')
class ServiceAuthorizationCapsElement extends HTMLElement {
  @target declare gridHost: HTMLElement;
  @target declare input: HTMLInputElement;
  private grid: AgGrid.GridApi<ServiceAuthorizationCapListItem> | undefined;

  connectedCallback() {
    agGrid.LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
    addEventListener('hashchange', this);
    this.initialize();
    this.input.form!.addEventListener(
      FORM_BEFORE_VALIDATE_SUBMIT_EVENT_TYPE,
      this
    );
    this.input.form!.addEventListener(FORM_VALIDATE_EVENT_TYPE, this);
  }

  disconnectedCallback() {
    removeEventListener('hashchange', this);
    this.grid?.destroy();
    this.input.form!.removeEventListener(
      FORM_BEFORE_VALIDATE_SUBMIT_EVENT_TYPE,
      this
    );
    this.input.form!.removeEventListener(FORM_VALIDATE_EVENT_TYPE, this);
  }

  handleEvent(event: Event) {
    switch (event.type) {
      case 'hashchange':
        this.focusCellForLocationHash();
        return;
      case FORM_BEFORE_VALIDATE_SUBMIT_EVENT_TYPE:
        this.grid?.stopEditing();
        return;
      case FORM_VALIDATE_EVENT_TYPE: {
        const errors = (event as CustomEvent<{ errors: FormValidationError[] }>)
          .detail.errors;
        const { start_date: authStartDate, end_date: authEndDate } =
          this.getAuthorizationInfo();
        const gridRows: Array<{
          rowIndex: number;
          data: ServiceAuthorizationCapListItem;
        }> = [];

        this.grid?.forEachNode(node => {
          if (typeof node.rowIndex === 'number' && node.data) {
            gridRows.push({ rowIndex: node.rowIndex, data: node.data });
          }
        });

        const invalidCell = (
          rowIndex: number,
          colKey: keyof ServiceAuthorizationCapListItem,
          message: string
        ) => {
          // this.grid?.startEditingCell({ rowIndex, colKey });
          this.grid?.deselectAll();
          this.grid?.clearRangeSelection();
          this.grid?.addCellRange({
            rowStartIndex: rowIndex,
            rowEndIndex: rowIndex,
            columnStart: colKey,
            columnEnd: colKey
          });
          this.grid?.setFocusedCell(rowIndex, colKey);
          errors.push({ message });
        };

        for (const row of gridRows) {
          const { rowIndex, data } = row;
          if (
            rowIndex >
            serviceAuthorizationLimits.service_authorization_caps_count
          ) {
            invalidCell(
              rowIndex,
              'start_date',
              `Cannot have more than ${serviceAuthorizationLimits.service_authorization_caps_count} caps.`
            );
            return;
          }
          if (!data.start_date) {
            invalidCell(rowIndex, 'start_date', 'Cap start date is required.');
            return;
          }
          if (!data.end_date) {
            invalidCell(rowIndex, 'end_date', 'Cap end date is required.');
            return;
          }
          if (data.amount === null || data.amount === undefined) {
            invalidCell(rowIndex, 'amount', 'Cap amount is required.');
            return;
          }
          if (data.amount < 0) {
            invalidCell(rowIndex, 'amount', 'Cap amount must be non-negative.');
            return;
          }
          if (data.amount > serviceAuthorizationLimits.maxAmount) {
            invalidCell(
              rowIndex,
              'amount',
              `Cap amount cannot exceed ${currencyFormat.format(serviceAuthorizationLimits.maxAmount)}.`
            );
            return;
          }
          if (
            data.approved_overage_amount === null ||
            data.approved_overage_amount === undefined
          ) {
            invalidCell(
              rowIndex,
              'approved_overage_amount',
              'Approved overage is required.'
            );
            return;
          }
          if (data.approved_overage_amount < 0) {
            invalidCell(
              rowIndex,
              'approved_overage_amount',
              'Approved overage must be non-negative.'
            );
            return;
          }
          if (
            data.approved_overage_amount > serviceAuthorizationLimits.maxAmount
          ) {
            invalidCell(
              rowIndex,
              'approved_overage_amount',
              `Approved overage cannot exceed ${currencyFormat.format(serviceAuthorizationLimits.maxAmount)}.`
            );
            return;
          }

          const capStartDate = new Date(data.start_date + 'T00:00:00Z');
          const capEndDate = new Date(data.end_date + 'T00:00:00Z');

          if (authStartDate && capStartDate < authStartDate) {
            invalidCell(
              rowIndex,
              'start_date',
              `Cap start date must be on or after authorization start date.`
            );
            return;
          }

          if (authEndDate && capEndDate > authEndDate) {
            invalidCell(
              rowIndex,
              'end_date',
              `Cap end date must be on or before authorization end date.`
            );
            return;
          }

          if (capStartDate > capEndDate) {
            invalidCell(
              rowIndex,
              'start_date',
              'Cap start date must be on or before cap end date.'
            );
            return;
          }
        }
        return;
      }
    }
  }

  initialize() {
    if (this.grid) {
      return;
    }
    const rowData: ServiceAuthorizationCapListItem[] = JSON.parse(
      this.input.value
    );
    const disabled = this.hasAttribute('disabled');
    const showExternal = rowData.some(item => item.external_usage);
    // const initialVisiblePromise = documentVisible().then(() =>
    //   elementVisible(this)
    // );

    const dateValueFormatter: AgGrid.ValueFormatterFunc<
      ServiceAuthorizationCapListItem,
      string
    > = ({ value }) => {
      if (!value) {
        return '';
      }
      return userUTCDateFormat.format(new Date(value + 'T00:00:00Z'));
    };

    const dateValueParser: AgGrid.ValueParserFunc<
      ServiceAuthorizationCapListItem,
      string
    > = ({ newValue }) => {
      if (!newValue) {
        return '';
      }
      const date = parseUserDate(newValue);
      return date ? date.toISOString().slice(0, 10) : null; // return null to reject
    };

    const getBalance = (data: ServiceAuthorizationCapListItem | undefined) =>
      typeof data?.amount === 'number' &&
      isFinite(data.amount) &&
      typeof data.usage === 'number' &&
      isFinite(data.usage)
        ? data.amount - data.usage // approved overage should not affect the balance or cumulative balance.
        : null;

    const amountValueParser: AgGrid.ValueParserFunc<
      ServiceAuthorizationCapListItem,
      number
    > = ({ newValue }) => {
      if (typeof newValue === 'number' && isFinite(newValue)) {
        return newValue;
      }

      if (typeof newValue !== 'string') {
        return null;
      }

      const trimmedValue = newValue.trim();
      if (!trimmedValue) {
        return null;
      }

      const isNegativeByParens =
        trimmedValue.startsWith('(') && trimmedValue.endsWith(')');
      const normalizedValue = trimmedValue
        .replace(/,/g, '')
        .replace(/[^0-9.-]/g, '');
      if (!normalizedValue) {
        return null;
      }

      const parsed = parseFloat(normalizedValue);
      if (!isFinite(parsed)) {
        return null;
      }

      return isNegativeByParens ? -Math.abs(parsed) : parsed;
    };

    const options: AgGrid.GridOptions<ServiceAuthorizationCapListItem> = {
      rowModelType: 'clientSide',
      rowData,
      getRowId: ({ data }) => data.service_authorization_cap_id,
      columnDefs: [
        {
          colId: 'start_date',
          field: 'start_date',
          headerName: loc_startDate[locale],
          valueFormatter: dateValueFormatter,
          valueParser: dateValueParser,
          cellEditorParams: { useFormatter: true, maxLength: 20 },
          width: 120,
          editable: !disabled
          // sort: 'asc'
        },
        {
          colId: 'end_date',
          field: 'end_date',
          headerName: loc_endDate[locale],
          valueFormatter: dateValueFormatter,
          valueParser: dateValueParser,
          cellEditorParams: { useFormatter: true, maxLength: 20 },
          width: 120,
          editable: !disabled,
          cellDataType: 'text'
        },
        {
          colId: 'amount',
          field: 'amount',
          headerName: loc_amount[locale],
          type: 'rightAligned',
          valueFormatter: ({ value }) =>
            typeof value === 'number' && isFinite(value)
              ? currencyFormat.format(value)
              : String(value ?? ''),
          valueParser: amountValueParser,
          width: 140,
          editable: !disabled,
          cellDataType: 'number',
          aggFunc: 'sum'
        },
        {
          colId: 'approved_overage_amount',
          field: 'approved_overage_amount',
          headerName: 'Approved overage',
          type: 'rightAligned',
          valueFormatter: ({ value }) =>
            typeof value === 'number' && isFinite(value)
              ? currencyFormat.format(value)
              : String(value ?? ''),
          valueParser: amountValueParser,
          width: 140,
          editable: !disabled,
          cellDataType: 'number',
          aggFunc: 'sum'
        },
        {
          colId: 'usage',
          field: 'usage',
          headerName: loc_usage[locale],
          type: 'rightAligned',
          valueFormatter: ({ value }) =>
            typeof value === 'number' && isFinite(value)
              ? currencyFormat.format(value)
              : String(value ?? ''),
          width: 140,
          aggFunc: 'sum'
        },
        {
          colId: 'balance',
          headerName: loc_balance[locale],
          type: 'rightAligned',
          valueGetter: ({ data }) => getBalance(data),
          valueFormatter: ({ value }) =>
            typeof value === 'number' && isFinite(value)
              ? currencyFormat.format(value)
              : String(value ?? ''),
          width: 140,
          aggFunc: 'sum'
        },
        {
          colId: 'cumulative_balance',
          headerName: loc_priorBalance[locale],
          type: 'rightAligned',
          valueGetter: ({ api, data, node }) => {
            if (!data?.start_date || node?.footer) {
              return null;
            }

            let total = 0;
            api.forEachNode(rowNode => {
              if (
                rowNode.footer ||
                !rowNode.data?.start_date ||
                rowNode.data.start_date >= data.start_date
              ) {
                return;
              }

              const balance = getBalance(rowNode.data);
              if (typeof balance === 'number' && isFinite(balance)) {
                total += balance;
              }
            });

            return total;
          },
          valueFormatter: ({ value }) =>
            typeof value === 'number' && isFinite(value)
              ? currencyFormat.format(value)
              : String(value ?? ''),
          width: 170
        },
        {
          colId: 'external_usage',
          field: 'external_usage',
          headerName: loc_externalUsage[locale],
          type: 'rightAligned',
          valueFormatter: ({ value }) => currencyFormat.format(value),
          width: 140,
          hide: !showExternal,
          aggFunc: 'sum'
        },
        {
          colId: 'external_id',
          field: 'external_id',
          headerName: loc_externalID[locale],
          width: 100,
          hide: !showExternal
        },
        {
          colId: 'actions',
          headerName: loc_actions[locale],
          flex: 1,
          sortable: false,
          filter: false,
          cellRenderer: (
            params: AgGrid.ICellRendererParams<ServiceAuthorizationCapListItem>
          ) => {
            if (params.node.footer || disabled) {
              return null;
            }
            return render(
              html`<button
                class="btn-octicon btn-octicon-danger"
                type="button"
                aria-label="${loc_delete[locale]}"
                data-grid-action="delete"
              >
                ${icon(trashIcon)}
              </button>`
            );
          },
          suppressNavigable: true,
          cellRendererParams: {
            suppressMouseEventHandling: () => true
          }
        }
      ],
      defaultColDef: {
        suppressHeaderMenuButton: true
      },
      rowClassRules: {
        'color-bg-subtle': ({ rowIndex }) => rowIndex % 2 !== 0
      },
      suppressExcelExport: true,
      suppressCsvExport: true,
      domLayout: 'autoHeight',
      popupParent: document.body,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      cellSelection: disabled
        ? false
        : {
            enableColumnSelection: false,
            handle: {
              mode: 'fill',
              direction: 'y'
              // suppressClearOnFillReduction: true
              // setFillValue: this.setFillValue
            }
          },
      onCellClicked: disabled ? undefined : this.onCellClicked,
      rowSelection: {
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: false,
        copySelectedRows: false
      },
      processDataFromClipboard: disabled
        ? undefined
        : this.processDataFromClipboard,
      onCellValueChanged: () => {
        this.refreshBalanceDependentCells();
        this.syncInputValue();
      },
      onModelUpdated: this.refreshCumulativeBalance,
      onRowDataUpdated: this.syncInputValue,
      onFirstDataRendered: () => this.focusCellForLocationHash(),
      grandTotalRow: 'pinnedBottom'
    };

    this.grid = agGrid.createGrid(this.gridHost, options);
  }

  addCap() {
    const id = crypto.randomUUID();
    this.grid?.applyTransaction({
      add: [
        {
          service_authorization_cap_id: id,
          start_date: null as any,
          end_date: null as any,
          amount_type: 'money',
          amount: null as any,
          usage: 0,
          approved_overage_amount: 0,
          external_usage: 0
        }
      ]
    });
    this.grid?.setFocusedCell(
      this.grid.getRowNode(id)!.rowIndex!,
      'start_date'
    );
  }

  addCaps(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const type: 'monthly-even' | 'calendar-day-prorated' | 'workday-prorated' =
      button.value as any;
    const { start_date, end_date, amount } = this.getAuthorizationInfo();

    if (!start_date || !end_date || start_date > end_date || amount === null) {
      alert(
        'Please enter a valid authorization start date, end date, and amount before creating monthly caps.'
      );
      return;
    }

    const toDateString = (value: Date) => value.toISOString().slice(0, 10);
    const startOfMonth = (value: Date) =>
      new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1));
    const endOfMonth = (value: Date) =>
      new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0));
    const maxDate = (a: Date, b: Date) => (a > b ? a : b);
    const minDate = (a: Date, b: Date) => (a < b ? a : b);
    const countWeekdaysInclusive = (rangeStart: Date, rangeEnd: Date) => {
      let count = 0;
      const day = new Date(rangeStart);
      while (day <= rangeEnd) {
        const weekDay = day.getUTCDay();
        if (weekDay !== 0 && weekDay !== 6) {
          count++;
        }
        day.setUTCDate(day.getUTCDate() + 1);
      }
      return count;
    };

    const add: ServiceAuthorizationCapListItem[] = [];

    const monthRanges: Array<{ start: Date; end: Date }> = [];
    let monthPointer = startOfMonth(start_date);
    const endMonth = startOfMonth(end_date);
    while (monthPointer <= endMonth) {
      const monthStart = maxDate(start_date, startOfMonth(monthPointer));
      const monthEnd = minDate(end_date, endOfMonth(monthPointer));
      monthRanges.push({ start: monthStart, end: monthEnd });
      monthPointer = new Date(
        Date.UTC(
          monthPointer.getUTCFullYear(),
          monthPointer.getUTCMonth() + 1,
          1
        )
      );
    }

    if (!monthRanges.length) {
      return;
    }

    const cents = Math.round(amount * 100);
    const weights = monthRanges.map(({ start, end }) => {
      if (type === 'calendar-day-prorated') {
        return dateDiffDays(start, end) + 1;
      }
      if (type === 'workday-prorated') {
        return countWeekdaysInclusive(start, end);
      }
      return 1;
    });
    const totalWeight = weights.reduce((acc, current) => acc + current, 0);

    if (totalWeight <= 0) {
      alert('Unable to create caps for this date range.');
      return;
    }

    const capCents = monthRanges.map((_range, index) => {
      if (index === monthRanges.length - 1) {
        return 0;
      }
      return Math.round((cents * weights[index]) / totalWeight);
    });
    const allocatedCents = capCents.reduce((acc, current) => acc + current, 0);
    capCents[capCents.length - 1] = cents - allocatedCents;

    monthRanges.forEach(({ start, end }, index) => {
      add.push({
        service_authorization_cap_id: crypto.randomUUID(),
        start_date: toDateString(start),
        end_date: toDateString(end),
        amount_type: 'money',
        amount: capCents[index] / 100,
        usage: 0,
        approved_overage_amount: 0,
        external_usage: 0
      });
    });

    this.grid?.applyTransaction({ add });
  }

  private focusCellForLocationHash() {
    const match = location.hash.match(/^#cap:(\d{4}-\d{2}-\d{2})$/);
    const grid = this.grid;
    if (!grid || !match) {
      return;
    }
    const capStartDate = match[1];
    let rowIndex: number | null = null;
    grid.forEachNodeAfterFilterAndSort(node => {
      if (
        rowIndex === null &&
        typeof node.rowIndex === 'number' &&
        node.data?.start_date === capStartDate
      ) {
        rowIndex = node.rowIndex;
      }
    });

    if (rowIndex === null) {
      return;
    }

    const colKey = 'approved_overage_amount';
    grid.ensureIndexVisible(rowIndex, 'middle');
    grid.deselectAll();
    grid.clearCellSelection();
    grid.addCellRange({
      rowStartIndex: rowIndex,
      rowEndIndex: rowIndex,
      columnStart: colKey,
      columnEnd: colKey
    });
    grid.setFocusedCell(rowIndex, colKey);
  }

  private refreshCumulativeBalance = () => {
    this.grid?.refreshCells({ columns: ['cumulative_balance'], force: true });
  };

  private refreshBalanceDependentCells = () => {
    this.grid?.refreshCells({
      columns: ['balance', 'cumulative_balance', 'actions'],
      force: true
    });
  };

  private getRows() {
    const rows: ServiceAuthorizationCapListItem[] = [];
    this.grid?.forEachNode(node => {
      if (node.data) {
        rows.push(node.data);
      }
    });
    return rows;
  }

  private syncInputValue = () => {
    if (!this.grid) {
      return;
    }
    const rowData = this.getRows();
    this.input.value = JSON.stringify(rowData);
    dispatchChange(this.input);
  };

  private onCellClicked = (
    params: AgGrid.CellClickedEvent<ServiceAuthorizationCapListItem>
  ) => {
    const action =
      params.event?.target instanceof Element &&
      params.event.target
        .closest('[data-grid-action]')
        ?.getAttribute('data-grid-action');

    if (action === 'delete') {
      if (params.data) {
        if (params.data.external_usage) {
          alert('Cannot delete cap with external usage.');
          return;
        }
        params.api.applyTransaction({ remove: [params.data] });
      }
    }
  };

  private processDataFromClipboard = (
    params: AgGrid.ProcessDataFromClipboardParams<ServiceAuthorizationCapListItem>
  ): string[][] | null => {
    const data = [...params.data];
    const emptyLastRow =
      data[data.length - 1][0] === '' && data[data.length - 1].length === 1;

    if (emptyLastRow) {
      data.splice(data.length - 1, 1);
    }

    const lastIndex = params.api!.getDisplayedRowCount() - 1;
    const focusedCell = params.api!.getFocusedCell();
    const focusedIndex = focusedCell!.rowIndex;

    if (focusedIndex + data.length - 1 > lastIndex) {
      const resultLastIndex = focusedIndex + (data.length - 1);
      const numRowsToAdd = resultLastIndex - lastIndex;

      const rowsToAdd: any[] = [];
      for (let i = 0; i < numRowsToAdd; i++) {
        rowsToAdd.push({
          service_authorization_cap_id: crypto.randomUUID(),
          start_date: null as any,
          end_date: null as any,
          amount_type: 'money',
          amount: null as any,
          usage: 0,
          approved_overage_amount: 0,
          external_usage: 0
        } satisfies ServiceAuthorizationCapListItem);
      }

      params.api!.applyTransaction({ add: rowsToAdd });
    }

    return data;
  };

  private getAuthorizationInfo() {
    // M/d/yyyy
    const start_date_string = (
      this.input.form!.elements.namedItem('start_date:date') as HTMLInputElement
    ).value;
    // yyyy-MM-dd (local)
    const start_date_local = parseUserDate(start_date_string);
    // M/d/yyyy
    const end_date_string = (
      this.input.form!.elements.namedItem('end_date:date') as HTMLInputElement
    ).value;
    // yyyy-MM-dd (local)
    const end_date_local = parseUserDate(end_date_string);
    // UTC
    const start_date = start_date_local
      ? new Date(start_date_local.toISOString().substr(0, 10))
      : null;
    // UTC
    const end_date = end_date_local
      ? new Date(end_date_local.toISOString().substr(0, 10))
      : null;

    const amount_string = (
      this.input.form!.elements.namedItem('amount:number') as HTMLInputElement
    ).value;
    let amount: number | null = null;
    if (amount_string) {
      const parsed = parseFloat(amount_string);
      if (isFinite(parsed)) {
        amount = parsed;
      }
    }
    return { start_date, end_date, amount };
  }
}

declare global {
  interface Window {
    ServiceAuthorizationCapsElement: typeof ServiceAuthorizationCapsElement;
  }
  interface HTMLElementTagNameMap {
    'service-authorization-caps': ServiceAuthorizationCapsElement;
  }
}
